import {subscriptionKind} from '../../constants';
import {subscriptionIsActive} from '../../util/special-event-access';

export const hasActiveSubscriptionForCatalogItem = (item, subscriptions, now = new Date()) => {
  if (!item || !subscriptions || !Array.isArray(subscriptions.subscriptions)) {
    return false;
  }

  const activeSubscriptions = subscriptions.subscriptions.filter(subscription => (
    subscription && subscriptionIsActive(subscription, now)
  ));

  if (activeSubscriptions.some(subscription => subscription.kind === subscriptionKind.rtvUnlimited)) {
    return item.contentType !== 'academy_slot';
  }

  const offers = item.commerceFlags && item.commerceFlags.subscriptionOffers
    ? item.commerceFlags.subscriptionOffers
    : [];

  return offers.some(offer => activeSubscriptions.some(subscription => {
    if (offer.kind === 'rtv_dedicated') {
      return subscription.kind === subscriptionKind.rtvDedicated
        && Number(subscription.partner) === Number(offer.partnerId)
        && Number(subscription.rtv_channel_id) === Number(offer.channelId);
    }

    if (offer.kind === 'rtv_special_event') {
      return subscription.kind === subscriptionKind.rtvEvent
        && Number(subscription.partner) === Number(offer.partnerId)
        && Number(subscription.rtv_channel_id) === Number(offer.channelId)
        && (!offer.accessStartsAt || new Date(subscription.created).getTime() === new Date(offer.accessStartsAt).getTime())
        && (!offer.accessEndsAt || new Date(subscription.expiry).getTime() === new Date(offer.accessEndsAt).getTime());
    }

    return false;
  }));
};

export const resolvePublicPartnerVideoCardState = ({
  access,
  accessLoading,
  actionLoading,
  activeSubscription,
  freeContent,
  item,
  pickleballVideo,
  section
}) => {
  const hasLegacyPlayback = Boolean(item.playback && item.playback.krpanoToken);
  const hasAccessFromPlayIntent = Boolean(access && access.action === 'play_allowed');
  const hasAccessFromCatalog = Boolean(item.userAccess && item.userAccess.canPlay === true);
  const isSlotContent = item.contentType !== 'partner_video';
  const playbackInfo = item.playback || {};
  const hasExplicitPlaybackMaterial = playbackInfo.hasPlaybackMaterial !== undefined;
  const hasPlaybackMaterial = hasExplicitPlaybackMaterial
    ? playbackInfo.hasPlaybackMaterial === true
    : hasLegacyPlayback;
  const playbackStatus = playbackInfo.status || null;
  const playbackStatusLabel = {
    scheduled: 'Video programmato',
    live_waiting_upload: 'In attesa di upload',
    waiting_upload: 'In attesa di upload',
    live_uploading: 'Upload in corso',
    uploading: 'Upload in corso',
    processing: 'Video in elaborazione',
    ready: 'Video pronto',
    unavailable: 'Video non disponibile'
  }[playbackStatus];
  const playbackStatusBadgeVariant = {
    scheduled: 'secondary',
    live_waiting_upload: 'secondary',
    waiting_upload: 'secondary',
    live_uploading: 'info',
    uploading: 'info',
    processing: 'warning',
    ready: 'success',
    unavailable: 'danger'
  }[playbackStatus] || 'secondary';
  const scheduled = isSlotContent && (playbackStatus === 'scheduled' || section === 'upcoming');
  const playbackBlocked = ['live_waiting_upload', 'waiting_upload', 'unavailable'].includes(playbackStatus);
  const videoUnavailable = isSlotContent
    && section !== 'upcoming'
    && (playbackBlocked || (hasExplicitPlaybackMaterial && !hasPlaybackMaterial));
  const waitingUpload = playbackStatus === 'live_waiting_upload' || playbackStatus === 'waiting_upload';
  const unavailableActionLabel = waitingUpload
    ? 'In attesa di upload'
    : 'Video non disponibile';
  const hasAccess = !videoUnavailable
    && (hasLegacyPlayback || hasAccessFromPlayIntent || hasAccessFromCatalog || activeSubscription);
  const subscriptionOffers = item.commerceFlags
    ? item.commerceFlags.subscriptionOffers || []
    : [];
  const singlePurchaseAvailable = Boolean(
    item.commerceFlags && item.commerceFlags.singlePurchaseAvailable
  );
  const hasVisionOptions = singlePurchaseAvailable || subscriptionOffers.length > 0;
  const shouldOpenVisionOptions = !videoUnavailable
    && !accessLoading
    && !hasAccess
    && !freeContent
    && hasVisionOptions;
  const accessVideoComplete = access && access.playback
    ? access.playback.videoComplete
    : undefined;
  const hasExplicitVideoComplete = item.videoComplete !== undefined
    || accessVideoComplete !== undefined;
  const videoComplete = hasExplicitVideoComplete
    ? item.videoComplete === true || accessVideoComplete === true
    : hasLegacyPlayback
      ? item.playback.videoComplete !== false
      : Boolean(access && access.action === 'play_allowed' && section === 'replay');
  const canCreateHighlight = hasAccess && isSlotContent && !scheduled;
  const videoReadyForMyVideo = hasAccess && isSlotContent && videoComplete && !scheduled;
  const showProcessingAction = hasAccess && isSlotContent && !videoReadyForMyVideo && !scheduled;
  const canCreateAnalysis = !videoUnavailable
    && pickleballVideo
    && isSlotContent
    && (hasAccess || freeContent)
    && section !== 'upcoming';
  const primaryActionLabel = actionLoading
    ? 'Verifica in corso...'
    : videoUnavailable
      ? unavailableActionLabel
      : scheduled && !shouldOpenVisionOptions
        ? 'Video programmato'
      : hasAccess
        ? 'Guarda la partita'
        : accessLoading
          ? 'Verifica accesso...'
          : freeContent
            ? 'Guarda la partita'
            : hasVisionOptions
              ? 'Scopri le opzioni di visione'
              : 'Verifica accesso';

  return {
    canCreateAnalysis,
    canCreateHighlight,
    hasAccess,
    hasVisionOptions,
    isSlotContent,
    playbackStatus,
    playbackStatusBadgeVariant,
    playbackStatusLabel,
    primaryActionDisabled: videoUnavailable || actionLoading || (scheduled && !shouldOpenVisionOptions),
    primaryActionLabel,
    shouldOpenVisionOptions,
    showProcessingAction,
    singlePurchaseAvailable,
    subscriptionOffers,
    unavailableActionLabel,
    videoReadyForMyVideo,
    videoUnavailable
  };
};
